import React, { useEffect, useState, useContext, useMemo } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import haxios from '../../lib/haxios';
import { Loader2, ChevronDown, ChevronUp, DollarSign, TrendingUp, Users, Target, Building2, Percent } from 'lucide-react';
import clsx from 'clsx';
import useApi from '../../hooks/useApi';

const BranchPerformance = () => {
    const [allBranchData, setAllBranchData] = useState({});
    const [loading, setLoading] = useState(true);
    const [expandedBranches, setExpandedBranches] = useState(new Set());
    const [activeTabs, setActiveTabs] = useState({}); // Track active tab per branch

    const { uinfo } = useContext(GlobalContext);
    const { data: branches = [], isLoading: branchesLoading } = useApi({ 
        url: '/salesassist/cplusassist/branches', 
        initial: [] 
    });
    
    // Fetch staff data for all branches
    const { data: allStaffData = [], isLoading: staffLoading } = useApi({ 
        url: `/salesassistnew/ustafferators`, 
        initial: [] 
    });
    
    // Fetch branch settings to get targets
    const { data: branchSettings = [], isLoading: branchSettingsLoading } = useApi({
        url: `/salesassist/cplusassist/branches`,
        initial: []
    });

    // Fetch year-to-date performance data for all branches (sum of all months in current year)
    useEffect(() => {
        const fetchYearToDateBranchData = async () => {
            try {
                setLoading(true);
                const branchCodes = branches.map(b => b.MamCode || b.Branch).filter(Boolean);
                
                if (branchCodes.length === 0) {
                    setLoading(false);
                    return;
                }

                // Get current year and current month
                const currentDate = new Date();
                const currentYear = currentDate.getFullYear();
                const currentMonth = currentDate.getMonth() + 1; // 1-12

                // Fetch monthly data for each branch for all months in current year (January through current month)
                const branchDataPromises = branchCodes.map(async (branchCode) => {
                    try {
                        // Fetch data for each month from January to current month
                        const monthPromises = [];
                        for (let month = 1; month <= currentMonth; month++) {
                            monthPromises.push(
                                haxios.get(`/visiondashboard/salesassist/getSales/${currentYear}/${month}/${branchCode}`)
                                    .catch(error => {
                                        console.error(`Failed to fetch data for ${branchCode}, month ${month}:`, error);
                                        return []; // Return empty array on error
                                    })
                            );
                        }

                        const monthlyResults = await Promise.all(monthPromises);
                        
                        // Debug: Check how many months have data
                        const monthsWithData = monthlyResults.filter(m => Array.isArray(m) && m.length > 0).length;
                        console.log(`BranchPerformance - ${branchCode}: Fetched ${currentMonth} months, ${monthsWithData} months have data`);
                        
                        // Aggregate monthly data to calculate year-to-date totals
                        let totalSales = 0;
                        let totalCost = 0;
                        let totalCredits = 0;
                        let invoiceCount = 0;

                        monthlyResults.forEach((monthData, monthIndex) => {
                            if (Array.isArray(monthData) && monthData.length > 0) {
                                let monthSales = 0;
                                let monthCost = 0;
                                monthData.forEach(day => {
                                    const daySales = parseFloat(day.sales || 0);
                                    const dayCost = parseFloat(day.cost || 0);
                                    totalSales += daySales;
                                    totalCost += dayCost;
                                    totalCredits += parseFloat(day.credits || 0);
                                    invoiceCount += parseInt(day.invoices || 0);
                                    monthSales += daySales;
                                    monthCost += dayCost;
                                });
                                console.log(`BranchPerformance - ${branchCode}, Month ${monthIndex + 1}: Sales=${monthSales.toFixed(2)}, Cost=${monthCost.toFixed(2)}, Margin=${(monthSales - monthCost).toFixed(2)}`);
                            } else {
                                console.log(`BranchPerformance - ${branchCode}, Month ${monthIndex + 1}: No data`);
                            }
                        });

                        // Calculate margin as Sales - Cost (not using API's marginPercent)
                        const totalMargin = totalSales - totalCost;
                        console.log(`BranchPerformance - ${branchCode} Year-to-Date: Sales=${totalSales.toFixed(2)}, Cost=${totalCost.toFixed(2)}, Margin=${totalMargin.toFixed(2)}`);
                        
                        // Net Sales = Overall Sales + Credits (credits are negative, so this reduces the total)
                        const netSales = totalSales + totalCredits;
                        
                        // Calculate margin percent - allow negative values
                        const marginPercent = totalSales > 0 ? (totalMargin / totalSales) * 100 : 0;
                        
                        // Calculate average invoice value
                        const avgInvoiceValue = invoiceCount > 0 ? netSales / invoiceCount : 0;
                        
                        // Calculate credit to sales ratio
                        const creditToSalesRatio = totalSales > 0 ? (Math.abs(totalCredits) / totalSales) * 100 : 0;

                        return {
                            branchCode,
                            data: {
                                totalSales,
                                netSales,
                                totalCost,
                                totalMargin,
                                totalCredits,
                                marginPercent,
                                invoiceCount,
                                avgInvoiceValue,
                                creditToSalesRatio,
                                // Keep parts data from original API if needed (will be 0 for now)
                                partsQtySold: 0,
                                partsQtyCredited: 0,
                                creditCount: 0,
                                avgLineValue: 0,
                                linesPerInvoice: 0,
                                partsReturnRate: 0
                            }
                        };
                    } catch (error) {
                        console.error(`Failed to fetch year-to-date data for ${branchCode}:`, error);
                        return { branchCode, data: null };
                    }
                });

                const results = await Promise.all(branchDataPromises);
                const dataMap = {};
                results.forEach(({ branchCode, data }) => {
                    if (data) {
                        // Store the data object directly (not wrapped in { branchCode, data })
                        dataMap[branchCode] = data;
                    }
                });
                console.log('BranchPerformance - Setting allBranchData:', dataMap);
                setAllBranchData(dataMap);
            } catch (error) {
                console.error('Failed to fetch year-to-date branch performance data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (branches.length > 0) {
            fetchYearToDateBranchData();
        }
    }, [branches]);

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num || 0);
    };

    const formatPercent = (num) => {
        return new Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).format(num || 0) + '%';
    };

    // Calculate overall business metrics (aggregated across all branches) - using year-to-date data
    const overallBusinessMetrics = useMemo(() => {
        // Aggregate year-to-date sales data from all branches
        let totalSales = 0;
        let totalNetSales = 0;
        let totalCost = 0;
        let totalCredits = 0;
        let totalInvoices = 0;
        let totalPartsSold = 0;
        let totalPartsCredited = 0;
        let totalCreditCount = 0;

        Object.values(allBranchData).forEach(branchData => {
            if (branchData) {
                totalSales += parseFloat(branchData.totalSales || 0);
                totalNetSales += parseFloat(branchData.netSales || 0);
                totalCost += parseFloat(branchData.totalCost || 0);
                totalCredits += parseFloat(branchData.totalCredits || 0);
                totalInvoices += (branchData.invoiceCount || 0);
                totalPartsSold += parseFloat(branchData.partsQtySold || 0);
                totalPartsCredited += parseFloat(branchData.partsQtyCredited || 0);
                totalCreditCount += (branchData.creditCount || 0);
            }
        });

        // Calculate margin as Sales - Cost (not using API's marginPercent field)
        const totalMargin = totalSales - totalCost;
        
        console.log('BranchPerformance - Overall Business Metrics:', {
            totalSales,
            totalCost,
            totalMargin,
            branchCount: Object.keys(allBranchData).length
        });

        // Calculate overall staff costs
        const totalStaffCosts = allStaffData.reduce((sum, staff) => {
            return sum + (parseFloat(staff.MonthlyCostPerHead) || 0);
        }, 0);

        const totalStaffCount = allStaffData.length;
        const avgCostPerStaff = totalStaffCount > 0 ? totalStaffCosts / totalStaffCount : 0;

        // Calculate average margin target (weighted by branch)
        let totalMarginTarget = 0;
        let branchCount = 0;
        branches.forEach(branch => {
            const branchSetting = branchSettings.find(b => b.MamCode === branch.MamCode || b.Branch === branch.Branch);
            if (branchSetting?.MarginTarget) {
                totalMarginTarget += parseFloat(branchSetting.MarginTarget);
                branchCount++;
            }
        });
        const avgMarginTarget = branchCount > 0 ? totalMarginTarget / branchCount : 0;

        // Calculate required turnover (using average margin target)
        let requiredTurnover = 0;
        if (avgMarginTarget > 0) {
            requiredTurnover = totalStaffCosts / (1 - (avgMarginTarget / 100));
        } else {
            requiredTurnover = totalStaffCosts; // Break even at minimum
        }

        // Calculate profit/loss (using year-to-date sales as monthly estimate)
        // Since we have year-to-date data, estimate monthly by dividing by number of months passed
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const estimatedMonthlySales = currentMonth > 0 ? totalSales / currentMonth : totalSales;
        const profitLoss = estimatedMonthlySales - totalStaffCosts;

        // Calculate margin percent - allow negative values
        const marginPercent = totalSales > 0 ? (totalMargin / totalSales) * 100 : 0;

        return {
            totalSales,
            totalNetSales,
            totalMargin,
            totalCredits,
            totalInvoices,
            totalPartsSold,
            totalPartsCredited,
            totalCreditCount,
            totalStaffCosts,
            totalStaffCount,
            avgCostPerStaff,
            requiredTurnover,
            profitLoss,
            marginPercent,
            estimatedMonthlySales
        };
    }, [allBranchData, allStaffData, branches, branchSettings]);

    // Calculate metrics for a specific branch - using year-to-date data
    const getBranchMetrics = (branchCode) => {
        const branchData = allBranchData[branchCode];
        if (!branchData) return null;

        const branchStaff = allStaffData.filter(staff => staff.Branch === branchCode);
        const totalStaffCosts = branchStaff.reduce((sum, staff) => {
            return sum + (parseFloat(staff.MonthlyCostPerHead) || 0);
        }, 0);

        const branchSetting = branchSettings.find(b => b.MamCode === branchCode || b.Branch === branchCode);
        const marginTarget = branchSetting?.MarginTarget || 0;
        const monthlySalesTarget = branchSetting?.MonthlySalesTarget || null;

        let requiredTurnover = 0;
        if (monthlySalesTarget) {
            requiredTurnover = monthlySalesTarget;
        } else if (marginTarget > 0) {
            requiredTurnover = totalStaffCosts / (1 - (marginTarget / 100));
        } else {
            requiredTurnover = totalStaffCosts;
        }

        // Estimate monthly sales from year-to-date data
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const estimatedMonthlySales = currentMonth > 0 ? parseFloat(branchData.totalSales || 0) / currentMonth : parseFloat(branchData.totalSales || 0);
        const profitLoss = estimatedMonthlySales - totalStaffCosts;

        // Calculate average cost per staff member
        const avgCostPerStaff = branchStaff.length > 0 ? totalStaffCosts / branchStaff.length : 0;

        // branchData already contains year-to-date totals with correct margin calculations
        // totalMargin and marginPercent are already calculated correctly in the fetch function
        return {
            ...branchData,
            totalStaffCosts,
            staffCount: branchStaff.length,
            avgCostPerStaff,
            requiredTurnover,
            profitLoss,
            estimatedMonthlySales
        };
    };

    const toggleBranch = (branchCode) => {
        setExpandedBranches(prev => {
            const newSet = new Set(prev);
            if (newSet.has(branchCode)) {
                newSet.delete(branchCode);
            } else {
                newSet.add(branchCode);
            }
            return newSet;
        });
    };

    const setActiveTab = (branchCode, tab) => {
        setActiveTabs(prev => ({
            ...prev,
            [branchCode]: tab
        }));
    };

    if (loading || staffLoading || branchSettingsLoading || branchesLoading) {
        return (
            <div className="flex items-center justify-center h-48 bg-white rounded-lg shadow">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="space-y-4 mb-8">
            {/* Overall Business KPI Cards - 8 Cards */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Overall Business Performance</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
                    {/* Existing 4 Sales Metrics */}
                    <KPICard
                        label="Total Sales"
                        value={`£${formatNumber(overallBusinessMetrics.totalSales)}`}
                        subValue={`${overallBusinessMetrics.totalInvoices} invoices`}
                        icon={<DollarSign className="w-6 h-6 text-blue-600" />}
                        className="text-blue-600"
                        borderColor="border-l-blue-500"
                    />
                    <KPICard
                        label="Net Sales"
                        value={`£${formatNumber(overallBusinessMetrics.totalNetSales)}`}
                        subValue={`£${formatNumber(overallBusinessMetrics.totalSales > 0 ? overallBusinessMetrics.totalNetSales / overallBusinessMetrics.totalInvoices : 0)} avg/invoice`}
                        icon={<DollarSign className="w-6 h-6 text-indigo-600" />}
                        className="text-indigo-600"
                        borderColor="border-l-indigo-500"
                    />
                    {uinfo.Status === 'admin' && (
                        <KPICard
                            label="Margin"
                            value={`£${formatNumber(overallBusinessMetrics.totalMargin)}`}
                            subValue={`${formatPercent(overallBusinessMetrics.marginPercent)} margin`}
                            icon={<Percent className={`w-6 h-6 ${overallBusinessMetrics.marginPercent >= 0 ? 'text-green-600' : 'text-red-600'}`} />}
                            className={overallBusinessMetrics.marginPercent >= 0 ? 'text-green-600' : 'text-red-600'}
                            borderColor={overallBusinessMetrics.marginPercent >= 0 ? 'border-l-green-500' : 'border-l-red-500'}
                        />
                    )}
                    <KPICard
                        label="Credits"
                        value={`£${formatNumber(overallBusinessMetrics.totalCredits)}`}
                        subValue={`${formatPercent(overallBusinessMetrics.totalSales > 0 ? (overallBusinessMetrics.totalCredits / overallBusinessMetrics.totalSales) * 100 : 0)} of sales`}
                        icon={<DollarSign className="w-6 h-6 text-red-600" />}
                        className="text-red-600"
                        borderColor="border-l-red-500"
                    />
                    
                    {/* New 4 Profitability Metrics */}
                    <KPICard
                        label="Total Staff Costs"
                        value={`£${formatNumber(overallBusinessMetrics.totalStaffCosts)}`}
                        subValue={`${overallBusinessMetrics.totalStaffCount} staff members`}
                        icon={<Users className="w-6 h-6 text-red-600" />}
                        className="text-red-600"
                        borderColor="border-l-red-500"
                    />
                    <KPICard
                        label="Required Turnover"
                        value={`£${formatNumber(overallBusinessMetrics.requiredTurnover)}`}
                        subValue="To break even"
                        icon={<Target className="w-6 h-6 text-orange-600" />}
                        className="text-orange-600"
                        borderColor="border-l-orange-500"
                    />
                    <KPICard
                        label="Profit/Loss"
                        value={`£${formatNumber(overallBusinessMetrics.profitLoss)}`}
                        subValue={overallBusinessMetrics.profitLoss >= 0 ? 'In profit' : 'At loss'}
                        icon={<TrendingUp className="w-6 h-6 text-green-600" />}
                        className={overallBusinessMetrics.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}
                        borderColor={overallBusinessMetrics.profitLoss >= 0 ? 'border-l-green-500' : 'border-l-red-500'}
                    />
                    <KPICard
                        label="Avg Cost Per Staff"
                        value={`£${formatNumber(overallBusinessMetrics.avgCostPerStaff)}`}
                        subValue="Monthly average"
                        icon={<Users className="w-6 h-6 text-purple-600" />}
                        className="text-purple-600"
                        borderColor="border-l-purple-500"
                    />
                </div>
            </div>

            {/* Branch Cards - Collapsible */}
            <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">Branch Performance</h2>
                {branches.map((branch) => {
                    const branchCode = branch.MamCode || branch.Branch;
                    const branchMetrics = getBranchMetrics(branchCode);
                    const isExpanded = expandedBranches.has(branchCode);
                    const activeTab = activeTabs[branchCode] || 'overview';

                    if (!branchMetrics) return null;

                    return (
                        <div key={branchCode} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                            <div 
                                className="p-4 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                                onClick={() => toggleBranch(branchCode)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {isExpanded ? (
                                            <ChevronUp className="h-5 w-5 text-gray-500" />
                                        ) : (
                                            <ChevronDown className="h-5 w-5 text-gray-500" />
                                        )}
                                        <Building2 className="h-5 w-5 text-gray-500" />
                                        <h3 className="text-lg font-semibold text-gray-900">{branchCode}</h3>
                                        <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                            {branchMetrics.staffCount} staff
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-gray-900">
                                            £{formatNumber(branchMetrics.totalSales)}
                                        </div>
                                        <div className="text-xs text-gray-500">Year-to-Date Sales</div>
                                    </div>
                                </div>
                            </div>
                            
                            {isExpanded && (
                                <div className="p-6">
                                    {/* Tab Buttons */}
                                    <div className="flex gap-2 mb-4">
                                        {['overview', 'parts'].map((tab) => (
                                            <button
                                                key={tab}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveTab(branchCode, tab);
                                                }}
                                                className={clsx(
                                                    'px-4 py-2 rounded-lg font-medium transition-colors capitalize',
                                                    activeTab === tab
                                                        ? 'bg-blue-500 text-white'
                                                        : 'bg-gray-100 hover:bg-gray-200'
                                                )}
                                            >
                                                {tab}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Tab Content */}
                                    {activeTab === 'overview' && (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <Metric
                                                label="Total Sales"
                                                value={`£${formatNumber(branchMetrics.totalSales)}`}
                                                subValue={`${branchMetrics.invoiceCount} invoices`}
                                            />
                                            <Metric
                                                label="Net Sales"
                                                value={`£${formatNumber(branchMetrics.netSales)}`}
                                                subValue={`£${formatNumber(branchMetrics.avgInvoiceValue)} avg/invoice`}
                                            />
                                            {uinfo.Status === 'admin' && (
                                                <Metric
                                                    label="Margin"
                                                    value={`£${formatNumber(branchMetrics.totalMargin)}`}
                                                    subValue={`${formatPercent(branchMetrics.marginPercent)} margin`}
                                                    className={branchMetrics.marginPercent >= 0 ? 'text-green-600' : 'text-red-600'}
                                                />
                                            )}
                                            <Metric
                                                label="Credits"
                                                value={`£${formatNumber(branchMetrics.totalCredits)}`}
                                                subValue={`${formatPercent(branchMetrics.creditToSalesRatio)} of sales`}
                                                className="text-red-600"
                                            />
                                        </div>
                                    )}

                                    {activeTab === 'parts' && (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <Metric
                                                label="Parts Sold"
                                                value={Number(branchMetrics.partsQtySold ?? 0).toFixed(0)}
                                                subValue={`£${formatNumber(branchMetrics.avgLineValue)} avg/line`}
                                            />
                                            <Metric
                                                label="Parts Credited"
                                                value={Number(branchMetrics.partsQtyCredited ?? 0).toFixed(0)}
                                                subValue={`${formatPercent(branchMetrics.partsReturnRate)} return rate`}
                                            />
                                            <Metric
                                                label="Lines/Invoice"
                                                value={formatNumber(branchMetrics.linesPerInvoice)}
                                                subValue="Average"
                                            />
                                            <Metric
                                                label="Credit Notes"
                                                value={branchMetrics.creditCount?.toLocaleString()}
                                                subValue="Count"
                                            />
                                        </div>
                                    )}

                                    {/* Profitability Metrics */}
                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                        <h4 className="text-md font-semibold text-gray-900 mb-4">Profitability Analysis</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                            <ProfitabilityMetric
                                                label="Required Turnover"
                                                value={`£${formatNumber(branchMetrics.requiredTurnover)}`}
                                                subValue="To break even"
                                                icon={<Target className="w-6 h-6 text-orange-600" />}
                                                className="text-orange-600"
                                            />
                                            <ProfitabilityMetric
                                                label="Profit/Loss"
                                                value={`£${formatNumber(branchMetrics.profitLoss)}`}
                                                subValue={branchMetrics.profitLoss >= 0 ? 'In profit' : 'At loss'}
                                                icon={<TrendingUp className="w-6 h-6 text-green-600" />}
                                                className={branchMetrics.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}
                                            />
                                            <ProfitabilityMetric
                                                label="Staff Costs (Monthly)"
                                                value={`£${formatNumber(branchMetrics.totalStaffCosts)}`}
                                                subValue={`${branchMetrics.staffCount} staff members`}
                                                icon={<DollarSign className="w-6 h-6 text-red-600" />}
                                                className="text-red-600"
                                            />
                                            <ProfitabilityMetric
                                                label="Avg Staff Member Cost"
                                                value={`£${formatNumber(branchMetrics.avgCostPerStaff || 0)}`}
                                                subValue="Per staff member"
                                                icon={<Users className="w-6 h-6 text-purple-600" />}
                                                className="text-purple-600"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const KPICard = ({ label, value, subValue, icon, className, borderColor }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 ${borderColor} p-6 hover:shadow-md transition-shadow`}>
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-600">{label}</div>
                <div className="p-2 bg-gray-50 rounded-lg">{icon}</div>
            </div>
            <div className={clsx('text-3xl font-bold', className)}>{value}</div>
            {subValue && (
                <div className="text-xs text-gray-500">{subValue}</div>
            )}
        </div>
    </div>
);

const Metric = ({ label, value, subValue, className }) => (
    <div className="bg-gray-50 p-4 rounded-lg">
        <div className="text-sm text-gray-500">{label}</div>
        <div className={clsx('text-xl font-bold mt-1', className)}>{value}</div>
        <div className="text-sm text-gray-500 mt-1">{subValue}</div>
    </div>
);

const ProfitabilityMetric = ({ label, value, subValue, icon, className }) => (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">{label}</div>
            <div className="p-2 bg-white rounded-lg">{icon}</div>
        </div>
        <div className={clsx('text-2xl font-bold', className)}>{value}</div>
        <div className="text-xs text-gray-500 mt-1">{subValue}</div>
    </div>
);

export default BranchPerformance;
